import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AutocompleteInput } from '@/components/ui/autocomplete-input';
import { Loader2, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { costoService } from '@/services/costoService';
import { useTramitesSearch, type TramiteSearchOption } from '@/hooks/useTramitesSearch';
import type { Costo } from '@/types/costo';

export type ModalCopiarModo = 'exportar' | 'importar';

interface ModalCopiarPresupuestoProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (otraTramiteId: string) => Promise<void>;
  tramiteActualId: string;
  modo: ModalCopiarModo;
  isLoading?: boolean;
}

function formatMonto(monto: number, moneda: string): string {
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(monto) + ` ${moneda}`;
}

function PreviewItems({ items, isLoading }: { items: Costo[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando items...
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        Esta escritura no tiene items de presupuesto.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        {items.length} item{items.length !== 1 ? 's' : ''} a copiar:
      </p>
      <div className="max-h-72 overflow-y-auto rounded-md border divide-y text-sm">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between px-3 py-2">
            <span className="min-w-0 truncate mr-2 font-medium" title={item.nombre}>{item.nombre}</span>
            <div className="flex items-center gap-2 shrink-0">
              <span
                className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                  item.tipo === 'ingreso'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {item.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
              </span>
              <span className="text-muted-foreground text-xs tabular-nums">
                {formatMonto(item.monto, item.moneda)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const ModalCopiarPresupuesto: React.FC<ModalCopiarPresupuestoProps> = ({
  isOpen,
  onClose,
  onConfirm,
  tramiteActualId,
  modo,
  isLoading = false,
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [tramiteSeleccionado, setTramiteSeleccionado] = useState<TramiteSearchOption | null>(null);
  const [error, setError] = useState('');

  const { opciones, isLoading: isBuscando } = useTramitesSearch(busqueda);

  // Para exportar: la fuente son los items del trámite actual (ya en caché por useCostos).
  // Para importar: la fuente son los items del trámite seleccionado.
  const sourceTramiteId =
    modo === 'exportar' ? tramiteActualId : tramiteSeleccionado?.id ?? null;

  const { data: previewData, isLoading: isLoadingPreview } = useQuery({
    queryKey: ['costos', sourceTramiteId],
    queryFn: () => costoService.getCostos(sourceTramiteId!),
    enabled: !!sourceTramiteId,
    staleTime: 1000 * 60 * 5,
  });

  const itemsPreview: Costo[] = previewData?.costos ?? [];

  // Para exportar mostramos preview siempre. Para importar, solo cuando hay tramite seleccionado.
  const mostrarPreview = modo === 'exportar' || !!tramiteSeleccionado;

  // Excluir la escritura actual de las opciones
  const opcionesFiltradas = opciones.filter((o) => o.id !== tramiteActualId);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (!isOpen) {
      setBusqueda('');
      setTramiteSeleccionado(null);
      setError('');
    }
  }, [isOpen]);

  const titulo =
    modo === 'exportar'
      ? 'Copiar presupuesto a otra escritura'
      : 'Importar presupuesto desde otra escritura';

  const labelBusqueda =
    modo === 'exportar' ? 'Escritura destino *' : 'Escritura origen *';

  const placeholderBusqueda =
    modo === 'exportar'
      ? 'Buscar escritura destino...'
      : 'Buscar escritura origen por nombre o ID...';

  const handleSelect = (opcion: TramiteSearchOption | null) => {
    setTramiteSeleccionado(opcion);
    if (opcion) {
      setBusqueda(opcion.label);
      setError('');
    }
  };

  const handleConfirm = async () => {
    if (!tramiteSeleccionado && modo === 'importar') {
      setError('Seleccioná una escritura para continuar.');
      return;
    }

    if (!isLoadingPreview && itemsPreview.length === 0) {
      setError(
        modo === 'exportar'
          ? 'La escritura actual no tiene items de presupuesto para copiar.'
          : 'La escritura origen no tiene items de presupuesto para copiar.'
      );
      return;
    }

    try {
      await onConfirm(tramiteSeleccionado?.id ?? tramiteActualId);
    } catch {
      setError('Error al copiar el presupuesto. Intentá nuevamente.');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const puedeConfirmar =
    !isLoading &&
    !isLoadingPreview &&
    itemsPreview.length > 0 &&
    (modo === 'exportar' || !!tramiteSeleccionado);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="grid-cols-1 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {modo === 'exportar' ? (
              <ArrowUpFromLine className="h-4 w-4" />
            ) : (
              <ArrowDownToLine className="h-4 w-4" />
            )}
            {titulo}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            {modo === 'exportar'
              ? 'Los items del presupuesto actual se agregarán a la escritura destino sin borrar lo existente.'
              : 'Los items del presupuesto de la escritura origen se agregarán al presupuesto actual sin borrar lo existente.'}
          </p>

          {/* Buscador de escritura (solo en importar; en exportar el origen es el trámite actual) */}
          {modo === 'importar' && (
            <div className="space-y-2">
              <Label>{labelBusqueda}</Label>
              <AutocompleteInput<TramiteSearchOption>
                value={busqueda}
                onChange={(v) => {
                  setBusqueda(v);
                  if (tramiteSeleccionado && v !== tramiteSeleccionado.label) {
                    setTramiteSeleccionado(null);
                  }
                  setError('');
                }}
                onSelect={handleSelect}
                options={opcionesFiltradas}
                getOptionLabel={(o) => o.label}
                placeholder={placeholderBusqueda}
                isLoading={isBuscando}
                emptyMessage="No se encontraron escrituras."
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Escribí el nombre de propiedad, persona involucrada o el ID numérico de la escritura.
              </p>
            </div>
          )}

          {/* Buscador de escritura destino (solo en exportar) */}
          {modo === 'exportar' && (
            <div className="space-y-2">
              <Label>{labelBusqueda}</Label>
              <AutocompleteInput<TramiteSearchOption>
                value={busqueda}
                onChange={(v) => {
                  setBusqueda(v);
                  if (tramiteSeleccionado && v !== tramiteSeleccionado.label) {
                    setTramiteSeleccionado(null);
                  }
                  setError('');
                }}
                onSelect={handleSelect}
                options={opcionesFiltradas}
                getOptionLabel={(o) => o.label}
                placeholder="Buscar escritura destino por nombre o ID..."
                isLoading={isBuscando}
                emptyMessage="No se encontraron escrituras."
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Escribí el nombre de propiedad, persona involucrada o el ID numérico de la escritura.
              </p>
            </div>
          )}

          {/* Preview de items */}
          {mostrarPreview && (
            <div className="space-y-2">
              <Label>
                {modo === 'exportar' ? 'Items que se copiarán' : 'Items que se importarán'}
              </Label>
              <PreviewItems items={itemsPreview} isLoading={isLoadingPreview} />
            </div>
          )}

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 border border-destructive/20">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!puedeConfirmar}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {modo === 'exportar' ? 'Copiando...' : 'Importando...'}
              </>
            ) : modo === 'exportar' ? (
              'Copiar'
            ) : (
              'Importar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
